import { get } from "lodash-es";
import type { FieldPath, FieldValues } from "react-hook-form";
import { useFormState } from "react-hook-form";

import { FieldFeedback, type FieldFeedbackProps } from "@/design-system/FieldFeedback";

interface FormErrorWrapperProps<TFormValues extends FieldValues> {
  name: FieldPath<TFormValues>;
  "data-testid"?: string;
  subtext?: React.ReactNode;
  encircle?: boolean;
  hideErrorText?: boolean;
  errorId?: string;
  width?: FieldFeedbackProps["width"];
  maxHeight?: FieldFeedbackProps["maxHeight"];
  layer?: FieldFeedbackProps["layer"];
  pushToEnd?: boolean;
  children: React.ReactNode;
}

export function FormErrorWrapper<TFormValues extends FieldValues>({
  name,
  children,
  hideErrorText,
  errorId,
  subtext,
  encircle,
  width,
  maxHeight,
  layer,
  pushToEnd,
  "data-testid": dataTestId,
}: FormErrorWrapperProps<TFormValues>): React.ReactNode {
  "use no memo";

  // RHF mutates errors in place, so compiler memoization can cache a stale field lookup.
  // https://github.com/orgs/react-hook-form/discussions/12524
  const { errors } = useFormState<TFormValues>({ name: name });

  const error = name ? get(errors, name) : null;
  const isError = error != null;

  return (
    <FieldFeedback
      data-testid={dataTestId}
      error={!hideErrorText && isError ? (error.message as string) : undefined}
      errorId={errorId}
      supportingText={!hideErrorText ? subtext : undefined}
      invalid={isError}
      highlightInvalid={encircle}
      width={width}
      maxHeight={maxHeight}
      layer={layer}
      pushToEnd={pushToEnd}
    >
      {children}
    </FieldFeedback>
  );
}
